import React, { useState, useCallback } from "react";
import { Pressable, ScrollView, View, StyleSheet } from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { useNavigation } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { SpokeChangeType as SpokeChangeTypeValue } from "@screens/Onboarding/types";
import { fontFamily } from "@utils/styles/fonts";

const textPrimary = color.Gray20;
const textMuted = color.color_808080;
const borderDefault = color.color_D9D9D9;
const dividerColor = color.lineColor;
const shadowColor = color.black;

type SpokeChangeStackParamList = {
  SpokeChangeType: undefined;
  SpokeChangeListing: { changeType: SpokeChangeTypeValue };
  SpokeChangeSubmitted: undefined;
};

const CHANGE_OPTIONS: {
  type: SpokeChangeTypeValue;
  title: string;
  bullets: string[];
}[] = [
  {
    type: "TEMPORARY",
    title: "Temporary Spoke Change",
    bullets: [
      "Aap kisi dusre nearby hub se aaj delivery kar sakte hain.",
      "Kal subah automatic aapke original hub pe wapas aa jaayenge.",
      "Pending OFD ya recon ho to pehle complete kariye.",
    ],
  },
  {
    type: "PERMANENT",
    title: "Permanent Spoke Change",
    bullets: [
      "Aap permanently ek naye hub pe shift ho jaayenge.",
      "Naye hub ka executive approve karega.",
      "Payout plan aur Gullak balance naye spoke pe update hoga.",
    ],
  },
];

const SpokeChangeType: React.FC = () => {
  const navigation =
    useNavigation<
      StackNavigationProp<SpokeChangeStackParamList, "SpokeChangeType">
    >();

  const [selected, setSelected] = useState<SpokeChangeTypeValue>("TEMPORARY");

  // No Gullak-balance gate for either change type: the balance migrates to
  // the new spoke as part of the change (see the PERMANENT card bullets).
  const onContinue = useCallback(() => {
    navigation.navigate("SpokeChangeListing", { changeType: selected });
  }, [selected, navigation]);

  return (
    <>
      <View style={styles.container}>
        <HeaderWithBackAndTitle
          headertitle="Change Spoke"
          navigation={navigation}
          hideShadow
        />

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text variant="title_18" style={styles.pageTitle}>
            Select Change Type
          </Text>

          {CHANGE_OPTIONS.map((opt) => {
            const isActive = selected === opt.type;
            return (
              <Pressable
                key={opt.type}
                style={[styles.card, isActive && styles.cardSelected]}
                onPress={() => setSelected(opt.type)}
                accessibilityRole="radio"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={opt.title}
              >
                <View style={styles.cardHeader}>
                  <View
                    style={[
                      styles.radioOuter,
                      isActive && styles.radioOuterSelected,
                    ]}
                  >
                    {isActive && <View style={styles.radioInner} />}
                  </View>
                  <Text
                    variant="title_16"
                    style={[
                      styles.cardTitle,
                      isActive && styles.cardTitleSelected,
                    ]}
                  >
                    {opt.title}
                  </Text>
                </View>
                {opt.bullets.map((b, i) => (
                  <Text key={i} variant="body_text_3" style={styles.bullet}>
                    • {b}
                  </Text>
                ))}
              </Pressable>
            );
          })}
        </ScrollView>

        <View style={styles.bottomBar}>
          <Pressable
            style={styles.continueButton}
            onPress={onContinue}
            accessibilityRole="button"
            accessibilityLabel="Continue to select spoke"
          >
            <Text variant="title_16" style={styles.continueButtonText}>
              Continue
            </Text>
          </Pressable>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: color.white },
  scrollContent: { paddingHorizontal: 20, paddingTop: 24, paddingBottom: 108 },
  pageTitle: {
    color: textPrimary,
    marginBottom: 20,
    fontFamily: fontFamily.bold,
  },
  card: {
    borderWidth: 1.5,
    borderColor: borderDefault,
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
    backgroundColor: color.white,
  },
  cardSelected: {
    borderColor: color.primary,
    backgroundColor: color.primaryLight,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: borderDefault,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  radioOuterSelected: { borderColor: color.primary },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: color.primary,
  },
  cardTitle: {
    color: textMuted,
    flex: 1,
    fontFamily: fontFamily.semiBold,
  },
  cardTitleSelected: { color: textPrimary },
  bullet: {
    color: textMuted,
    marginBottom: 6,
    lineHeight: 20,
    paddingLeft: 4,
  },
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    borderTopColor: dividerColor,
    elevation: 12,
    shadowColor,
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
  },
  continueButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  continueButtonText: { color: color.white },
});

export default SpokeChangeType;
